import { Fragment, useEffect, useState } from 'react';
import { Badge, Collapse, Table } from 'reactstrap';
import Requester from '../shared/Requester';
import { formatearPuntaje } from '../wizard/utils';
import {
    DetalleRespuestasCuestionario,
    getDisplayNameActividad,
    resumenEvaluacion,
} from './cuestionarioRiesgoDisplay';

function idRegistro(registro) {
    return registro?._id ? String(registro._id) : '';
}

function formatearFecha(timestampUtc) {
    if (!timestampUtc) return '—';
    return new Date(timestampUtc).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

function badgeNivelResidual(nivel) {
    if (!nivel) return <span className="text-muted">—</span>;
    const esBajo = String(nivel).toUpperCase().includes('BAJO');
    return (
        <Badge color={esBajo ? 'success' : 'warning'} className="text-uppercase">
            {nivel}
        </Badge>
    );
}

export default function HistorialCuestionarioTable({ unidadId, respuestaActualId }) {
    const [registros, setRegistros] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);
    const [expandidoId, setExpandidoId] = useState(null);

    useEffect(() => {
        if (!unidadId) {
            setRegistros([]);
            setCargando(false);
            return;
        }

        setCargando(true);
        setError(null);
        Requester.get(`respuestas-cuestionario/historial?unidad_id=${unidadId}`)
            .then((res) => setRegistros(res?.data?.registros || []))
            .catch(() => setError('No se pudo cargar el historial de evaluaciones.'))
            .finally(() => setCargando(false));
    }, [unidadId]);

    const toggleDetalle = (id) => {
        setExpandidoId((prev) => (prev === id ? null : id));
    };

    if (cargando) {
        return <p className="text-muted small mb-4">Cargando historial...</p>;
    }

    if (error) {
        return <p className="text-danger small mb-4">{error}</p>;
    }

    if (!registros.length) {
        return <p className="text-muted small mb-4">No hay evaluaciones guardadas.</p>;
    }

    const actualId = respuestaActualId ? String(respuestaActualId) : null;

    return (
        <div className="mb-4">
            <h6 className="border-bottom pb-2 mb-3">Historial de evaluaciones del cuestionario</h6>
            <Table size="sm" bordered responsive className="align-middle mb-0">
                <thead className="table-light">
                    <tr>
                        <th>Fecha de respuesta</th>
                        <th>Actividad</th>
                        <th>Respondido por</th>
                        <th className="text-center">Riesgo inherente</th>
                        <th className="text-center">Eficacia controles</th>
                        <th className="text-center">Riesgo residual</th>
                        <th className="text-center">Resultado matriz</th>
                        <th style={{ width: 110 }} />
                    </tr>
                </thead>
                <tbody>
                    {registros.map((registro) => {
                        const id = idRegistro(registro);
                        const { cuestionario = [], mitigantes = [], actividad_vulnerable, timestamp_utc, usuario } = registro;
                        const { puntaje, promedioEficacia, riesgoResidual, diagnosticoResidual } = resumenEvaluacion(
                            cuestionario,
                            mitigantes
                        );
                        const esActual = actualId && id === actualId;
                        const abierto = expandidoId === id;

                        return (
                            <Fragment key={id}>
                                <tr className={esActual ? 'table-primary' : undefined}>
                                    <td>
                                        {formatearFecha(timestamp_utc)}
                                        {esActual && (
                                            <Badge className="ms-2 badge-outline-sistema">Actual</Badge>
                                        )}
                                    </td>
                                    <td>{getDisplayNameActividad(actividad_vulnerable)}</td>
                                    <td>{usuario?.nombre || '—'}</td>
                                    <td className="text-center">{formatearPuntaje(puntaje)}</td>
                                    <td className="text-center">
                                        {promedioEficacia !== null ? `${formatearPuntaje(promedioEficacia)}%` : '—'}
                                    </td>
                                    <td className="text-center">{formatearPuntaje(riesgoResidual)}</td>
                                    <td className="text-center">
                                        {badgeNivelResidual(diagnosticoResidual?.nivel)}
                                    </td>
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => toggleDetalle(id)}
                                            aria-expanded={abierto}
                                        >
                                            {abierto ? 'Ocultar' : 'Detalle'}
                                        </button>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={8} className="p-0 border-0">
                                        <Collapse isOpen={abierto}>
                                            <div className="p-3 bg-light border-bottom">
                                                <DetalleRespuestasCuestionario
                                                    cuestionario={cuestionario}
                                                    mitigantes={mitigantes}
                                                />
                                            </div>
                                        </Collapse>
                                    </td>
                                </tr>
                            </Fragment>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
}
